import { useNavigate } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Package, ArrowRight, Loader2 } from 'lucide-react';
import { useLowStockProducts, getStockUrgency } from '@/hooks/queries/useLowStockProducts';

export function StockAlertsWidget() {
  const navigate = useNavigate();
  const { data: lowStockProducts = [], isLoading } = useLowStockProducts(5);

  const urgencyColors = {
    critical: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400',
    low: 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/20 dark:text-orange-400',
    normal: 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400',
  };

  const urgencyIcons = {
    critical: '🔴',
    low: '🟠',
    normal: '🟡',
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-orange-600" />
        </CardContent>
      </Card>
    );
  }

  if (lowStockProducts.length === 0) {
    return (
      <Card className="border-green-200 dark:border-green-800">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Package className="w-5 h-5 text-green-600" />
            تنبيهات المخزون / Stock Alerts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <div className="text-4xl mb-2">✅</div>
            <p className="text-green-600 font-medium">كل المنتجات متوفرة</p>
            <p className="text-sm text-gray-500">Tous les produits en stock</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-orange-200 dark:border-orange-800">
      <CardHeader>
        <CardTitle className="text-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-600" />
            تنبيهات المخزون / Stock Alerts
            <Badge variant="destructive" className="ml-2">
              {lowStockProducts.length}
            </Badge>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate({ to: '/apps/products' })}
            className="text-xs"
          >
            عرض الكل / Voir tout
            <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {lowStockProducts.map((product) => {
            const urgency = getStockUrgency(product.stock_quantity, product.min_stock);
            return (
              <div
                key={product.id}
                className={`flex items-center justify-between p-3 rounded-lg border ${urgencyColors[urgency]}`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{urgencyIcons[urgency]}</span>
                    <div>
                      <p className="font-medium text-sm">{product.name}</p>
                      <p className="text-xs opacity-75">{product.code}</p>
                    </div>
                  </div>
                </div>
                <div className="text-right mr-4">
                  <p className="text-xs opacity-75">المخزون / Stock</p>
                  <p className="font-bold">
                    {product.stock_quantity}
                    {product.stock_quantity === 0 && (
                      <span className="ml-1 text-xs font-normal">(نفذ / Épuisé)</span>
                    )}
                  </p>
                  <p className="text-xs opacity-60">Min: {product.min_stock}</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate({ to: `/apps/products` })}
                  className="ml-3"
                >
                  إعادة طلب
                </Button>
              </div>
            );
          })}
        </div>

        {lowStockProducts.length >= 5 && (
          <Button
            variant="outline"
            className="w-full mt-4"
            onClick={() => navigate({ to: '/apps/products' })}
          >
            عرض جميع المنتجات المنخفضة / Voir tous les produits bas
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
